import {
  backend,
  API_URL,
  AUTH_REFRESH_ENDPOINT,
  AUTH_OBTAIN_ENDPOINT,
  REGISTER_ENDPOINT,
} from "../config";

let isRefreshing = false;
let pendingQueue = [];

const processQueue = (error, token = null) => {
  pendingQueue.forEach(({ resolve, reject }) => {
    error ? reject(error) : resolve(token);
  });
  pendingQueue = [];
};

const isTokenExpired = (currUser) => {
  if (!currUser) return true;
  const duration = currUser.expires_in * 1000;
  const diff = new Date().getTime() - currUser.last_refresh;
  return diff >= duration;
};

const applyToken = (access) => {
  const currUser = JSON.parse(localStorage.getItem("user"));
  if (currUser) {
    currUser.access = access;
    currUser.last_refresh = new Date().getTime();
    localStorage.setItem("user", JSON.stringify(currUser));
  }
  backend.defaults.headers.common["Authorization"] = `Bearer ${access}`;
};

export const logout = () => {
  localStorage.removeItem("user");
  delete backend.defaults.headers.common["Authorization"];
  return Promise.resolve();
};

export const refreshToken = () => {
  const currUser = JSON.parse(localStorage.getItem("user"));
  const data = JSON.stringify({ refresh: currUser.refresh });

  return backend.post(`${API_URL}${AUTH_REFRESH_ENDPOINT}`, data, {
    headers: {
      "Content-Type": "application/json",
    },
  });
};

export const getUser = () => {
  let currUser = JSON.parse(localStorage.getItem("user"));

  if (!currUser) {
    return Promise.resolve(null);
  }

  if (isTokenExpired(currUser)) {
    return refreshToken()
      .then((response) => {
        applyToken(response.data.access);
        const updated = JSON.parse(localStorage.getItem("user"));
        return { data: JSON.stringify(updated) };
      })
      .catch(() => {
        logout();
        return null;
      });
  }

  backend.defaults.headers.common["Authorization"] =
    `Bearer ${currUser.access}`;
  return Promise.resolve({ data: JSON.stringify(currUser) });
};

export const login = (username, password) => {
  const data = JSON.stringify({ username, password });

  return backend
    .post(`${API_URL}${AUTH_OBTAIN_ENDPOINT}`, data, {
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then(async (response) => {
      response.data.last_refresh = new Date().getTime();
      localStorage.setItem("user", JSON.stringify(response.data));

      return getUser();
    });
};

export const register = (data) => {
  const payload = JSON.stringify({
    email: data.email,
    phone_number: data.phone,
    password: data.password,
    password_confirm: data.confirmPassword,
    restaurant_name: data.restaurantName,
    zip_code: data.zipCode,
    shipping_address: data.shippingAddress,
    billing_address: data.billingAddress,
    operating_hours: data.operatingHours,
  });

  return backend.post(`${API_URL}${REGISTER_ENDPOINT}`, payload, {
    headers: {
      "Content-Type": "application/json",
    },
  });
};

backend.interceptors.request.use(
  async (config) => {
    const isAuthEndpoint =
      config.url?.includes(AUTH_REFRESH_ENDPOINT) ||
      config.url?.includes(AUTH_OBTAIN_ENDPOINT);
    if (isAuthEndpoint) return config;

    const currUser = JSON.parse(localStorage.getItem("user"));
    if (!currUser) return config;
    if (!isTokenExpired(currUser)) return config;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push({ resolve, reject });
      })
        .then((token) => {
          config.headers.Authorization = `Bearer ${token}`;
          return config;
        })
        .catch((err) => Promise.reject(err));
    }

    isRefreshing = true;
    try {
      const response = await refreshToken();
      const newToken = response.data.access;
      applyToken(newToken);
      processQueue(null, newToken);
      config.headers.Authorization = `Bearer ${newToken}`;
      return config;
    } catch (error) {
      processQueue(error, null);
      logout();
      return Promise.reject(error);
    } finally {
      isRefreshing = false;
    }
  },
  (error) => Promise.reject(error),
);

backend.interceptors.response.use(
  (response) => response,
  (error) => {
    const isAuthEndpoint =
      error.config?.url?.includes(AUTH_REFRESH_ENDPOINT) ||
      error.config?.url?.includes(AUTH_OBTAIN_ENDPOINT);

    if (error.response?.status === 401 && !isAuthEndpoint) {
      logout();
      window.location = "/";
    }

    if (
      error.response?.status === 401 &&
      !isAuthEndpoint &&
      !error.config?.url?.includes("barcodealternates")
    ) {
      logout();
    }

    return Promise.reject(error);
  },
);
